import { useEffect, useMemo, useRef, useState } from 'react'
import { DesktopOutlined, SmileOutlined } from '@ant-design/icons'
import { capitalize } from 'lodash'
import SignalBarsIcon from 'Assets/icons/signal-bars.svg?react'
import styles from './ProctoringPermissionsGate.module.less'
import { NetworkStrength } from 'Components/Proctoring/proctoring.types'

type NetworkConnection = {
    effectiveType?: string
    addEventListener?: (type: string, listener: () => void) => void
    removeEventListener?: (type: string, listener: () => void) => void
}

const getNetworkConnection = (): NetworkConnection | undefined => {
    const nav = navigator as Navigator & {
        connection?: NetworkConnection
        mozConnection?: NetworkConnection
        webkitConnection?: NetworkConnection
    }
    return nav.connection || nav.mozConnection || nav.webkitConnection
}

const getCurrentStrength = (): NetworkStrength => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return NetworkStrength.Poor
    const connection = getNetworkConnection()
    if (!connection) return NetworkStrength.Good
    if (connection.effectiveType) {
        if (connection.effectiveType === '4g') return NetworkStrength.Good
        if (connection.effectiveType === '3g') return NetworkStrength.Fair
        return NetworkStrength.Poor
    }
    return NetworkStrength.Good
}

const STRENGTH_CLASS: Record<NetworkStrength, string> = {
    [NetworkStrength.Good]: styles.strengthGood,
    [NetworkStrength.Fair]: styles.strengthFair,
    [NetworkStrength.Poor]: styles.strengthPoor,
}

const StrengthBadge = () => {
    const [strength, setStrength] = useState(getCurrentStrength)

    useEffect(() => {
        const update = () => setStrength(getCurrentStrength())
        const connection = getNetworkConnection()
        connection?.addEventListener?.('change', update)
        window.addEventListener('online', update)
        window.addEventListener('offline', update)
        return () => {
            connection?.removeEventListener?.('change', update)
            window.removeEventListener('online', update)
            window.removeEventListener('offline', update)
        }
    }, [])

    const statusClass = STRENGTH_CLASS[strength]
    const statusText = useMemo(() => capitalize(strength), [strength])

    return (
        <div className={styles.strengthBadge}>
            <span className={styles.strengthLabel}>Signal Strength</span>
            <SignalBarsIcon className={statusClass} />
            <span className={`${styles.strengthStatus} ${statusClass}`}>{statusText}</span>
        </div>
    )
}

export const PermissionsDisplay = ({
    videoStream,
    screenStream,
    showVideoTile = true,
    showScreenTile = true,
}: {
    videoStream?: MediaStream | null
    screenStream?: MediaStream | null
    showVideoTile?: boolean
    showScreenTile?: boolean
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const screenRef = useRef<HTMLVideoElement | null>(null)

    useEffect(() => {
        const video = videoRef.current
        if (video && videoStream) {
            videoStream.getTracks().forEach(track => {
                if (!track.enabled) track.enabled = true
            })
            video.srcObject = videoStream
            void video.play().catch(() => {})
        } else if (video && !videoStream) {
            video.srcObject = null
        }
        return () => {
            if (video) video.srcObject = null
        }
    }, [videoStream])

    useEffect(() => {
        const screen = screenRef.current
        if (screen && screenStream) {
            screenStream.getTracks().forEach(track => {
                if (!track.enabled) track.enabled = true
            })
            screen.srcObject = screenStream
            void screen.play().catch(() => {})
        } else if (screen && !screenStream) {
            screen.srcObject = null
        }
        return () => {
            if (screen) screen.srcObject = null
        }
    }, [screenStream])

    let mainTile = null
    if (showVideoTile) {
        mainTile = videoStream ? (
            <video ref={videoRef} autoPlay playsInline muted className={styles.mainVideo} />
        ) : (
            <div className={styles.displayPlaceholder}>
                <SmileOutlined className={styles.displayPlaceholderIcon} />
            </div>
        )
    } else if (showScreenTile) {
        mainTile = screenStream ? (
            <video ref={screenRef} autoPlay playsInline muted className={styles.mainVideo} />
        ) : (
            <div className={styles.displayPlaceholder}>
                <DesktopOutlined className={styles.displayPlaceholderIcon} />
            </div>
        )
    }

    let screenThumb = null
    if (showScreenTile && showVideoTile) {
        const thumbContent = screenStream ? (
            <video ref={screenRef} autoPlay playsInline muted className={styles.screenThumbVideo} />
        ) : (
            <DesktopOutlined className={styles.screenThumbIcon} />
        )
        screenThumb = <div className={styles.screenThumb}>{thumbContent}</div>
    }

    return (
        <div className={styles.display} data-testid="proctoring-permissions-display">
            <div className={styles.strengthWrap}>
                <StrengthBadge />
            </div>
            {mainTile}
            {screenThumb}
        </div>
    )
}
